import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, AlertCircle, ShieldCheck, ArrowLeft, Loader2, Database, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { PortalNav } from "../components/shared/PortalNav";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [branding, setBranding] = useState({ name: "SteelOne ERP", logo_url: "" });
  const [step, setStep] = useState("credentials"); // "credentials" | "2fa"
  const [tempToken, setTempToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [userName, setUserName] = useState("");
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [databases, setDatabases] = useState([]);
  const [dbDisplayNames, setDbDisplayNames] = useState({});
  const [currentDb, setCurrentDb] = useState("");
  const [dbSwitching, setDbSwitching] = useState(false);
  const [showDbSelector, setShowDbSelector] = useState(false);
  const [domainBranding, setDomainBranding] = useState(null);
  const [forceShowDbSelector, setForceShowDbSelector] = useState(false);
  const { login, complete2FALogin } = useAuth();
  const navigate = useNavigate();

  // Check if ?select-db=true is in URL
  const selectDbParam = new URLSearchParams(window.location.search).get("select-db") === "true";

  useEffect(() => {
    setForceShowDbSelector(selectDbParam);
  }, [selectDbParam]);

  useEffect(() => {
    api.get("/company-profile/branding").then(res => setBranding(res.data)).catch(() => {});
    api.get("/auth/sso-status").then(res => setSsoEnabled(res.data.microsoft_sso_enabled)).catch(() => {});

    // Domain-based routing: resolve hostname to DB
    const hostname = window.location.hostname;
    api.get(`/database-manager/public/domain-resolve?hostname=${encodeURIComponent(hostname)}`).then(res => {
      const data = res.data;
      if (data.matched) {
        // Domain is mapped — per-session routing: select this DB instantly (no restart)
        setDomainBranding({
          company_name: data.company_name,
          tagline: data.tagline,
          description: data.description,
        });
        if (!selectDbParam && data.db_name) {
          localStorage.setItem("stancor_db", data.db_name);
          setCurrentDb(data.db_name);
        }
      }
    }).catch(() => {});

    // Load cached DB list immediately (survives backend restart)
    try {
      const cached = JSON.parse(localStorage.getItem("erp_db_list") || "null");
      if (cached?.databases?.length > 1) {
        setDatabases(cached.databases);
        setDbDisplayNames(cached.display_names || {});
        setCurrentDb(cached.current || "");
      }
    } catch {}

    const loadDatabases = (retries = 8) => {
      api.get("/database-manager/public/list").then(res => {
        const dbs = res.data.databases || [];
        const names = res.data.display_names || {};
        const cur = localStorage.getItem("stancor_db") || res.data.current || "";
        setDatabases(dbs);
        setDbDisplayNames(names);
        setCurrentDb(cur);
        localStorage.setItem("erp_db_list", JSON.stringify({ databases: dbs, display_names: names, current: cur }));
      }).catch(() => {
        if (retries > 0) setTimeout(() => loadDatabases(retries - 1), 2500);
      });
    };
    loadDatabases();

    // Check if first-time setup is needed
    api.get("/auth/setup-status").then(res => {
      if (res.data.setup_required) navigate("/setup", { replace: true });
    }).catch(() => {});
  }, [navigate, selectDbParam]);

  // Should DB selector be visible?
  const shouldShowDbList = forceShowDbSelector && databases.length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setUserName(result.user?.full_name || result.user?.email || email);
        setStep("2fa");
      } else {
        const parts = [];
        if (result.last_login_at) {
          parts.push(`Last login: ${new Date(result.last_login_at).toLocaleString()}${result.last_login_ip ? ` from ${result.last_login_ip}` : ""}`);
        }
        if (result.password_changed_at) {
          const days = Math.floor((Date.now() - new Date(result.password_changed_at).getTime()) / 86400000);
          parts.push(`Password last changed ${days === 0 ? "today" : `${days} day(s) ago`}`);
        }
        toast.success(`Login successful — welcome back, ${result.user?.full_name || ""}`, {
          description: parts.join(" · ") || undefined,
          duration: 6000,
        });
        navigate("/");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Login failed";
      if (status === 423) {
        setError("LOCKED:" + detail);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await complete2FALogin(tempToken, totpCode);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid authentication code");
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setTempToken("");
    setTotpCode("");
    setError("");
  };

  const handleMicrosoftLogin = async () => {
    setError("");
    setMsLoading(true);
    try {
      const res = await api.get("/auth/microsoft/login");
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Microsoft SSO is not configured.");
      setMsLoading(false);
    }
  };

  const handleDbSwitch = async (dbName) => {
    // Per-session routing: selecting a DB is instant — no server restart needed
    setShowDbSelector(false);
    setError("");
    localStorage.setItem("stancor_db", dbName);
    setCurrentDb(dbName);
    const cached = JSON.parse(localStorage.getItem("erp_db_list") || "{}");
    cached.current = dbName;
    localStorage.setItem("erp_db_list", JSON.stringify(cached));
  };

  const logoSrc = domainBranding?.logo_url
    ? domainBranding.logo_url.startsWith("/api")
      ? `${process.env.REACT_APP_BACKEND_URL}${domainBranding.logo_url}`
      : domainBranding.logo_url
    : null;

  // Use domain branding if available, otherwise fallback to company profile
  // Platform-branded login: SteelOne unless a custom-domain portal provides its own branding
  const displayName = domainBranding?.company_name || "SteelOne";
  const displayTagline = domainBranding?.tagline || "";
  const displayDescription = domainBranding?.description || "";

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1a1f] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" className="w-10 h-10 rounded-md object-contain bg-white/10" data-testid="login-logo-img" />
            ) : (
              <img src="/steelone-logo.png" alt="SteelOne" className="w-10 h-10 rounded-md object-contain bg-white p-0.5"
                   data-testid="login-logo-fallback" />
            )}
            <span className="text-white text-xl font-bold tracking-tight"
                  data-testid="login-company-name">
              {displayName}
            </span>
          </div>
          <p className="text-white/40 text-xs uppercase tracking-[0.3em] mt-1">
            {displayDescription || "Manufacturing · Trading · Distribution"}
          </p>
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="text-white text-4xl font-bold leading-tight">
            {displayTagline ? (
              <>
                {displayTagline.split(".")[0]}.<br />
                <span className="text-[#C41E3A]">{displayTagline.split(".").slice(1).join(".").trim() || "Seamless Operations."}</span>
              </>
            ) : (
              <>
                Manufacturing · Trading ·<br />
                <span className="text-[#C41E3A]">Distribution.</span>
              </>
            )}
          </h2>
          <p className="text-white/50 text-sm max-w-md leading-relaxed">
            {domainBranding ? `End-to-end management for ${displayName}.` : "One platform for the entire steel group."}
            {displayDescription ? ` ${displayDescription}.` : " From raw material to dispatch — every tonne tracked."}
          </p>
        </div>

        <p className="text-white/20 text-xs relative z-10">
          SteelOne ERP · Manufacturing · Trading · Distribution
        </p>
      </div>

      {/* Right panel - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" className="w-9 h-9 rounded-md object-contain" data-testid="login-logo-mobile" />
            ) : (
              <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                {displayName?.substring(0, 2).toUpperCase() || "ST"}
              </div>
            )}
            <span className="text-lg font-bold tracking-tight">
              {displayName} ERP
            </span>
          </div>

          <div>
            <h1 className="text-title font-heading tracking-tight">
              {step === "credentials" ? "Sign in" : "Two-Factor Authentication"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {step === "credentials"
                ? "Enter your credentials to access the system"
                : `Enter the code from your authenticator app for ${userName}`}
            </p>
          </div>

          {dbSwitching && (
            <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 p-3 rounded-md" data-testid="db-switching-banner">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              Switching database... Page will reload shortly.
            </div>
          )}

          {error && (
            <div className={`flex items-center gap-2 text-sm p-3 rounded-md ${
              error.startsWith("LOCKED:") 
                ? "text-amber-700 bg-amber-50 border border-amber-200" 
                : "text-destructive bg-destructive/10"
            }`}
                 data-testid="login-error">
              {error.startsWith("LOCKED:") 
                ? <Lock className="w-4 h-4 shrink-0" />
                : <AlertCircle className="w-4 h-4 shrink-0" />
              }
              {error.startsWith("LOCKED:") ? error.slice(7) : error}
            </div>
          )}

          {step === "credentials" ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              {shouldShowDbList && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Database</Label>
                  <div className="relative" data-testid="db-selector">
                    <Database className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <button type="button"
                      className="w-full h-11 pl-10 pr-10 rounded-md border border-input bg-background text-sm text-left flex items-center hover:border-primary/50 transition-colors"
                      onClick={() => setShowDbSelector(!showDbSelector)}
                      disabled={dbSwitching}
                      data-testid="db-selector-btn">
                      <span className="truncate">{currentDb ? (dbDisplayNames[currentDb] || currentDb) : "Select database"}</span>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    </button>
                    {showDbSelector && (
                      <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="db-dropdown">
                        {databases.map(db => (
                          <button key={db} type="button"
                            className={`w-full px-4 py-2.5 text-sm text-left hover:bg-muted flex items-center justify-between transition-colors ${db === currentDb ? "bg-primary/5 font-medium" : ""}`}
                            onClick={() => handleDbSwitch(db)}
                            data-testid={`db-option-${db}`}>
                            <span className="truncate">
                              {dbDisplayNames[db] || db}
                              {dbDisplayNames[db] && dbDisplayNames[db] !== db && (
                                <span className="ml-2 text-[10px] text-muted-foreground font-mono">{db}</span>
                              )}
                            </span>
                            {db === currentDb && <span className="text-[10px] text-primary font-semibold ml-2">ACTIVE</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 h-11 rounded-md"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pl-10 h-11 rounded-md"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-md font-semibold uppercase tracking-wider text-sm"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>

              {ssoEnabled && (
                <>
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-background px-3 text-muted-foreground">or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 rounded-md font-medium text-sm gap-3"
                    onClick={handleMicrosoftLogin}
                    disabled={msLoading}
                    data-testid="microsoft-sso-btn"
                  >
                    {msLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                      </svg>
                    )}
                    Sign in with Microsoft
                  </Button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={handle2FASubmit} className="space-y-5" data-testid="2fa-login-form">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-violet-600 dark:text-violet-400" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="totp" className="text-sm font-medium">Authentication Code</Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit code"
                    className="pl-10 h-11 rounded-md font-mono tracking-widest text-center text-lg"
                    data-testid="2fa-code-input"
                    autoFocus
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">You can also use a recovery code</p>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-md font-semibold uppercase tracking-wider text-sm"
                disabled={loading || totpCode.length < 6}
                data-testid="2fa-submit-btn"
              >
                {loading ? "Verifying..." : "Verify & Sign In"}
              </Button>

              <button
                type="button"
                onClick={backToCredentials}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </button>
            </form>
          )}

          <PortalNav active="erp" />
        </div>
      </div>
    </div>
  );
}
